import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { File, Image, Upload } from "lucide-react";
import React, { useState } from "react";
interface FileUploadDialogProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onFilesUpload: (files: File[]) => void;
  trigger?: React.ReactNode;
  title?: string;
  description?: string;
  accept?: string;
  maxFiles?: number;
  maxSizeInMB?: number;
  multiple?: boolean;
  showSelectedFiles?: boolean;
  helperText?: string;
  required?: boolean;
  uploadButtonText?: string;
  cancelButtonText?: string;
}
export const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
  isOpen,
  onOpenChange,
  onFilesUpload,
  title = "Upload Files",
  description = "Upload documents, media files, and attachments",
  accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.mov,.avi",
  maxFiles = 10,
  maxSizeInMB = 1000,
  multiple = true,
  showSelectedFiles = true,
  helperText = "Examples: Documents, Images, Videos, PDFs",
  required = false,
  uploadButtonText = "Upload Files",
  cancelButtonText = "Cancel",
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const formFields: FormField[] = [
    {
      name: "documents",
      type: "multi-file",
      label: "Select Files",
      className: "col-span-2",
      placeholder: "Choose files to upload",
      required: required,
      multiple: multiple,
      accept: accept,
      helperText: helperText,
      classes: FIELD_CLASSES,
      maxFiles: maxFiles,
      maxSize: maxSizeInMB,
    },
  ];
  const form = FormService.createForm({
    fields: formFields,
    defaultValues: {
      documents: selectedFiles,
    },
  });
  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "documents") {
      setSelectedFiles(value || []);
    }
  };
  const handleUpload = () => {
    if (selectedFiles.length > 0) {
      onFilesUpload(selectedFiles);
      setSelectedFiles([]);
      form.reset();
      onOpenChange?.(false);
    }
  };
  const handleCancel = () => {
    setSelectedFiles([]);
    form.reset();
    onOpenChange?.(false);
  };
  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
      return <Image className="h-5 w-5 text-blue-500" />;
    } else if (file.type === "application/pdf") {
      return <File className="h-5 w-5 text-red-500" />;
    }
    return <File className="h-5 w-5 text-gray-500" />;
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    // Update form value
    form.setValue("documents", newFiles);
  };
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          <DynamicForm
            fields={formFields}
            form={form}
            onFieldChange={handleFieldChange}
            config={{
              layout: "vertical",
              showSubmitButton: false,
              showResetButton: false,
              spacing: "medium",
            }}
          />
        </div>

        {/* Sticky footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
          <Button variant="outline" onClick={handleCancel}>
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            {selectedFiles.length > 0
              ? `${uploadButtonText} (${selectedFiles.length})`
              : uploadButtonText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default FileUploadDialog;
